/* eslint-disable jsx-a11y/label-has-for */
/* eslint-disable jsx-a11y/label-has-associated-control */

// Form fields

import Tooltip from "@JohnLewisPartnership/jl-design-system/dist/components/tooltip/Tooltip";
import InputWithCharacterCountdown from "@JohnLewisPartnership/jl-design-system/dist/elements/input-with-character-countdown/InputWithCharacterCountdown";
import { TextInput } from "@JohnLewisPartnership/jl-design-system/dist/components/text-input/TextInput";
import TextArea from "@JohnLewisPartnership/jl-design-system/dist/elements/textarea/Textarea";
// JL form fields
import EMAIL from "@JohnLewisPartnership/checkout-forms/dist/form/fields/email";
import { excludeSymbolsNames } from "@JohnLewisPartnership/checkout-forms/dist/form/normalizers/restrictInput";
import getRegExp, { lettersDotDashApostropheSpacePattern } from "@JohnLewisPartnership/checkout-forms/dist/form/regex";
import React from "react";
import ImageUpload from "../../image-upload/image-upload";

const supportedFileTypes = "image";
const lettersNumbersSpaceRegex = /^[A-Za-z0-9 ]+$/;
const phoneNumberRegex = /^\+?(\d+ ?){6,}$/;

const getComponent = component => {
  switch (component) {
    case "input":
      return TextInput;
    case "textarea":
      return TextArea;
    case "inputWithCharacterCountdown":
      return InputWithCharacterCountdown;
    default:
      return TextInput;
  }
};

const imageFormField = ({
  name,
  label,
  required,
  className,
  onAttachmentAdded,
  onAttachmentRemoved,
  attachments,
  attachmentError,
  description,
}) => ({
  component: ImageUpload,
  name,
  label,
  description,
  id: name,
  className: className || "fieldLarge",
  required: typeof required === "boolean" ? required : true,
  type: "file",
  maxUploads: 6,
  extensionProps: { accept: `${supportedFileTypes}/*,image/heic` },
  instruction: undefined,
  hideErrors: true,
  onAttachmentAdded,
  onAttachmentRemoved,
  attachments,
  supportedMediaTypePrefix: supportedFileTypes,
  meta: { error: attachmentError, touched: !!attachmentError },
});

const customFormField = ({
  name,
  label,
  type,
  required,
  inputType,
  maxLength,
  showCountdown,
  placeholder,
  regex,
  className,
  component,
  validationMessages,
  labelHelperElement,
  autoComplete,
}) => ({
  component: getComponent(component),
  name,
  label,
  id: name,
  type: type || "text",
  className: className || "fieldLarge",
  required: typeof required === "boolean" ? required : true,
  placeholder: placeholder || null,
  inputType: inputType || null,
  maxLength: maxLength || null,
  showCountdown: showCountdown || null,
  regex: regex || null,
  validationMessageKeys: {
    required: (validationMessages && validationMessages.required) || `Please enter ${label.toLowerCase()}`,
    maxLength: (validationMessages && validationMessages.maxLength) || "formValidationTooLong",
    regex: (validationMessages && validationMessages.regex) || "formValidationInvalidCharacters",
  },
  normalize: excludeSymbolsNames,
  labelHelperElement: labelHelperElement || null,
  autoComplete: autoComplete || null,
});

export default {
  CASE_NUMBER: customFormField({
    name: "caseNumber",
    component: "input",
    label: "Case number",
    hint: null,
    regex: /^CASE-((?!00000000|12345678)[0-9]{8})$/,
    validationMessages: {
      regex: "Case number must start with CASE- followed by 8 digits only.",
    },
    maxLength: 13,
    required: true,
    labelHelperElement: React.createElement(Tooltip, { id: "case-number" }, [
      React.createElement(
        "p",
        { key: "case-number" },
        `You can find your case number on your My Account page under 'View cases' as well as any emails relating to your issue. Each case number will start with 'CASE-'. If you are unable to remember your case number please use 'Email us' to contact us.`,
      ),
    ]),
  }),
  FULL_NAME: customFormField({
    name: "fullName",
    label: "First and last name",
    maxLength: 80,
    regex: getRegExp(lettersDotDashApostropheSpacePattern),
    validationMessages: {
      regex: "Please remove the invalid characters to continue",
    },
  }),
  POSTCODE: customFormField({
    name: "postcode",
    label: "Postcode",
    maxLength: 10,
    regex: lettersNumbersSpaceRegex,
    validationMessages: {
      regex: "Please remove the invalid characters to continue",
    },
  }),
  EMAIL: {
    ...EMAIL,
    name: "emailAddress",
    label: "Email",
    value: null,
  },
  ORDER_NUMBER: customFormField({
    name: "orderNumber",
    label: "Order number",
    hint: null,
    regex: /^[0-9]{8,10}$/,
    validationMessages: {
      regex: "Please enter an 8-10 digit order number",
    },
    maxLength: 10,
    required: true,
    labelHelperElement: React.createElement(Tooltip, {}, [
      React.createElement(
        "p",
        {},
        `This is an 8 or 9 digit number. You'll find it on your email confirmation or online when you sign into My Account in the 'online orders' area`,
      ),
    ]),
  }),
  ORDER_NUMBER_OPTIONAL: customFormField({
    name: "orderNumber",
    label: "Order number",
    hint: null,
    regex: /^[0-9]{8,10}$/,
    validationMessages: {
      regex: "Please enter an 8-10 digit order number",
    },
    maxLength: 10,
    required: false,
    labelHelperElement: React.createElement(Tooltip, {}, [
      React.createElement(
        "p",
        {},
        `This is an 8 or 9 digit number. You'll find it on your email confirmation or online when you sign into My Account in the 'online orders' area`,
      ),
    ]),
  }),
  CR_NUMBER_OPTIONAL: customFormField({
    name: "customerReferenceNumber",
    label: "Customer requirement / Order number",
    maxLength: 9,
    regex: /^[1-9]\d{7,8}$/,
    validationMessages: {
      regex: "Please enter an 8-9 digit customer requirement number (with no leading zeros)",
    },
    required: false,
    autoComplete: "off",
    labelHelperElement: React.createElement(Tooltip, {}, [
      React.createElement("p", {}, `This is an 8-digit number starting with 5 or 6.`),
      React.createElement(
        "p",
        {},
        `If you purchased a larger item or one that needs to be delivered to you, this number should be on the receipt or paperwork you received in-store.`,
      ),
      React.createElement("p", {}, `If you didn’t purchase this type of item, you can leave this field blank.`),
    ]),
  }),
  PURCHASE_LOCATION: customFormField({
    name: "purchaseLocation",
    label: "Purchase location",
    maxLength: 80,
  }),
  STOCK_NUMBER: customFormField({
    name: "stockNumber",
    label: "Product code",
    required: false,
    maxLength: 8,
    regex: /^[0-9]{8}$/,
    validationMessages: {
      regex: "Please enter a valid 8 digit product code",
    },
    labelHelperElement: React.createElement(Tooltip, {}, [
      React.createElement("p", {}, "You’ll find this number on any product page on our website."),
    ]),
  }),
  MY_JL_MEM_NUMBER: customFormField({
    name: "referenceNumber",
    label: "my John Lewis membership number",
    required: false,
    maxLength: 16,
    regex: /^([0-9]{11}|[0-9]{16})$/,
    validationMessages: {
      regex: "Please enter a valid 11 or 16 digit membership number, including leading zeros",
    },
  }),
  RETURN_NUMBER: customFormField({
    name: "referenceNumber",
    label: "Return number",
    required: false,
    maxLength: 80,
  }),
  VOUCHER_NUMBER: customFormField({
    name: "referenceNumber",
    label: "Gift card or voucher number",
    required: false,
    maxLength: 19,
    regex: /^[0-9]{10,19}$/,
    validationMessages: {
      regex: "Please enter a valid number. 19 digits for gift cards, or 10 digits for gift vouchers",
    },
  }),
  DESCRIPTION: customFormField({
    name: "description",
    label: "Your message",
    component: "inputWithCharacterCountdown",
    inputType: "textarea",
    maxLength: 3000,
    showCountdown: 3000,
    placeholder: "Tell us more about your query so we can respond as quickly and efficiently as possible.",
  }),
  PHONE_NUMBER_REQUIRED: customFormField({
    name: "phoneNumber",
    label: "Phone number",
    required: true,
    maxLength: 20,
    regex: phoneNumberRegex,
    validationMessages: {
      regex: "Please enter a valid phone number",
    },
  }),
  PHONE_NUMBER: customFormField({
    name: "phoneNumber",
    label: "Phone number",
    required: false,
    maxLength: 20,
    regex: phoneNumberRegex,
    validationMessages: {
      regex: "Please enter a valid phone number",
    },
  }),
  IMAGE_UPLOAD: (handleAttachmentAdded, handleAttachmentRemoved, attachments, attachmentError, description) =>
    imageFormField({
      name: "imageUpload",
      required: false,
      onAttachmentAdded: handleAttachmentAdded,
      onAttachmentRemoved: handleAttachmentRemoved,
      attachments,
      attachmentError,
      description,
    }),
};
